import { Controller, useForm } from 'react-hook-form';
import FlexFormContainer from '../common/FlexFormContainer';
import {
  Button,
  Heading,
  SwitchField,
} from '@aws-amplify/ui-react';
import { useProfileGeneralQuery } from './api/useProfileGeneralQuery';
import { useUserQuery } from '../common/useUserQuery';
import {
  PostGeneralMutation,
  PutGeneralMutation,
} from './api/useProfileGeneralMutation';
import { useNavigate } from 'react-router-dom';
import { generalResolver } from './validations/generalValidations';
import { zodResolver } from '@hookform/resolvers/zod';

const defaultValues = {
  allowVoiceMessage: 0,
  allowMailMessage: 0,
  allowEmailMessage: 0,
  allowSmsMessage: 0,
  allowHealthInfoExchange: 0,
  acceptHIPAANotice: 0,
  religion: '',
  race: '',
  language: '',
  employerName: '',
  employerAddr1: '',
  employerAddr2: '',
  employerCity: '',
  employerState: '',
  employerZip: '',
};

export default function General() {
  const navigate = useNavigate();
  const { data: userQueryData } = useUserQuery();
  const {
    mutate: putGeneral,
    isLoading: isLoadingPut,
    isSuccess: isSuccessPut,
  } = PutGeneralMutation();
  const {
    mutate: postGeneral,
    isLoading: isLoadingPost,
    isSuccess: isSuccessPost,
  } = PostGeneralMutation();

  const {
    isLoading,
    error,
    data: generalResult,
    isFetching,
    isSuccess,
  } = useProfileGeneralQuery(userQueryData?.patientUserId);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues,
    values: generalResult,
    resolver: zodResolver(generalResolver),
  });

  const onSubmit = (values) => {
    values.patientUserId = userQueryData?.patientUserId;
    if (generalResult && generalResult.patientUserId) {
      putGeneral({
        ...values,
      });
    } else {
      postGeneral({
        patientUserId: userQueryData?.patientUserId,
        ...values,
      });
    }
    reset();
    navigate('/user/quick-actions/profile-summary');
  };

  if (isLoading) return 'Loading...';

  if (isLoadingPost || isLoadingPut) return 'Saving changes...';

  if (isSuccess)
    return (
      <>
        <div>{isFetching ? 'Updating...' : ''}</div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FlexFormContainer headerTitle={' Communication Preferences'}>
            <Heading level={5}>
              Communication Preferences and HIE Preferences
            </Heading>
            <Controller
              name="allowVoiceMessage"
              control={control}
              render={({ field }) => (
                <SwitchField
                  label="Allow Voice Message"
                  labelPosition="end"
                  {...field}
                  isChecked={
                    field.value ?? generalResult?.allowVoiceMessage === 1
                  }
                  onChange={(event) =>
                    field.onChange(event.target.checked ? 1 : 0)
                  }
                />
              )}
            />
            <Controller
              name="allowMailMessage"
              control={control}
              render={({ field }) => (
                <SwitchField
                  label="Allow Mail Message"
                  labelPosition="end"
                  {...field}
                  isChecked={
                    field.value ?? generalResult?.allowMailMessage === 1
                  }
                  onChange={(event) =>
                    field.onChange(event.target.checked ? 1 : 0)
                  }
                />
              )}
            />
            <Controller
              name="allowEmailMessage"
              control={control}
              render={({ field }) => (
                <SwitchField
                  label="Allow Email Message"
                  labelPosition="end"
                  {...field}
                  isChecked={
                    field.value ?? generalResult?.allowEmailMessage === 1
                  }
                  onChange={(event) =>
                    field.onChange(event.target.checked ? 1 : 0)
                  }
                />
              )}
            />
            <Controller
              name="allowSmsMessage"
              control={control}
              render={({ field }) => (
                <SwitchField
                  label="Allow SMS Message"
                  labelPosition="end"
                  {...field}
                  isChecked={
                    field.value ?? generalResult?.allowSmsMessage === 1
                  }
                  onChange={(event) =>
                    field.onChange(event.target.checked ? 1 : 0)
                  }
                />
              )}
            />
            <Controller
              name="allowHealthInfoExchange"
              control={control}
              render={({ field }) => (
                <SwitchField
                  label="Allow Health Information Exchange"
                  labelPosition="end"
                  {...field}
                  isChecked={
                    field.value ?? generalResult?.allowHealthInfoExchange === 1
                  }
                  onChange={(event) =>
                    field.onChange(event.target.checked ? 1 : 0)
                  }
                />
              )}
            />
            <Controller
              name="acceptHIPAANotice"
              control={control}
              render={({ field }) => (
                <SwitchField
                  label="I Accept HIPAA Notice"
                  labelPosition="end"
                  {...field}
                  isChecked={
                    field.value ?? generalResult?.acceptHIPAANotice === 1
                  }
                  onChange={(event) =>
                    field.onChange(event.target.checked ? 1 : 0)
                  }
                />
              )}
            />
            <Button variation="primary" type="submit">
              Save
            </Button>
          </FlexFormContainer>
        </form>
      </>
    );
}
